import React, { useEffect, useRef, useState } from "react";
import KPITile from "../../../../../common/KPITile/KPITile";
import KPIDetailTile from "../../../../../common/KPIDetailTile/KPIDetailTile";
import Benchmark from "../../../../../common/Benchmark/Benchmark";
import VarianceValue from "../../../../../common/VarianceValue/VarianceValue";
import styles from "./FAAPointsTile.module.scss";
import { useTranslation } from "react-i18next";
import { useProfileParams } from "../../../../../../utils/common";
import { useAppSelector } from "../../../../../../redux/hooks";
import { useGetBusinessPerformanceDetailsQuery } from "../../../../../../redux/services/businessPerformaceAPI";
import ViewModeSwitcher, { ViewMode } from "../../../../../common/ViewModeSwitcher/ViewModeSwitcher";
import GraphCard from "../../../../../common/GraphCard/GraphCard";
import GenericTable from "../../../../../common/GenericTable/GenericTable";
import InsightAI from "../../../../../common/InsightAI/InsightAI";
import Skeleton from "../../../../../common/Skeleton/Skeleton";
import isEmpty from "lodash/isEmpty";
import { useGetAGCAIDetailsMutation } from "../../../../../../redux/services/agcAIAPI";
import { isValidLineChartData } from "../../../../../../utils/common";
import { NumberFormatter } from "../../../../../../utils/NumberFormatter";
interface SummaryProp {
  summary?: any;
  loading?: boolean;
}

export const FAAPointsTile: React.FC<SummaryProp> = ({ summary, loading = false }) => {
  const valueNum = typeof summary?.kpiValue === 'number' ? summary.kpiValue : 0;
  const benchmark = summary?.benchmark;
  const { t } = useTranslation();
  const comparisonCriteria = summary?.kpiVarianceComparisonCriteria ?? "yoy";
  const userDetails = useAppSelector((state: any) => state.userDetails.user);
  const [userAffiliateCode] = useState(userDetails?.userAffiliateCode || 0);

  return (
    <KPITile title={t('faaPoints.title_short')} dataTestId="faa-points-tile" loading={loading}>
      <div className={styles.tile_content}>
        <div className={styles.values_wrapper}>
          <div className={styles.value_wrapper}>
            <div className={styles.value_content}>{NumberFormatter(userAffiliateCode, valueNum)}</div>
            <VarianceValue
              value={summary?.kpiVarianceValue ?? 0}
              dataType={summary?.kpiVarianceDataType}
              currency={summary?.kpiCurrency}
              comparisonCriteria={comparisonCriteria}
            />
          </div>
        </div>
        {benchmark && (
          <div className={styles.bar_wrapper}>
            <Benchmark
              minValue={benchmark?.min || 0}
              maxValue={benchmark?.max || 0}
              averageValue={benchmark?.marketAverage}
              medianValue={benchmark?.median}
              quartileLine={[benchmark?.lowerQuartile ?? 0, benchmark?.upperQuartile ?? 0]}
              currentBenchmarkValue={valueNum}
              benchmarkDataType={summary?.benchmarkDataType}
              isPercentValue={false}
            />
          </div>
        )}
      </div>
    </KPITile>
  );
};

export const FAAPointsDetail: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
  const { affiliateCode, aboNumber } = useProfileParams();
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<ViewMode>("left");
  const kpiCode = "faaPoints";

  const { data: detailsResponse, isLoading } = useGetBusinessPerformanceDetailsQuery(
    { affiliateCode, aboNumber, payload: { kpis: [{ code: kpiCode }] } as any },
    { skip: !onClose }
  );

  const [getAGCAIDetails, { isLoading: isLoadingAiSummary }] = useGetAGCAIDetailsMutation();
  const [aiContent, setAiContent] = useState<string>("");
  const hasRequestedRef = useRef(false);

  useEffect(() => {
    // Reset ref when modal closes
    if (!onClose) {
      hasRequestedRef.current = false;
      setAiContent("");
      return;
    }

    const canFetch = Number(affiliateCode) !== 0 && aboNumber !== 0 && Boolean(onClose);
    if (!canFetch || hasRequestedRef.current) return;
    
    hasRequestedRef.current = true;
    getAGCAIDetails({ affiliateCode, aboNumber, payload: { kpis: [{ code: kpiCode }] } as any })
      .then((resp: any) => {
        // Handle different response structures
        const responseData = resp?.data || resp;
        const aiRaw = responseData?.body || responseData;
        
        // Try multiple extraction patterns
        const aiResolved = typeof aiRaw === "string" 
          ? aiRaw 
          : aiRaw?.[kpiCode] || aiRaw?.summary || aiRaw || "";
        
        const content = aiResolved ? String(aiResolved).slice(0, 300).concat("...") : "";
        setAiContent(content);
      })
      .catch((error: any) => {
        console.error("Error fetching AI details:", error);
        setAiContent("");
      });
  }, [affiliateCode, aboNumber, onClose, kpiCode, getAGCAIDetails]);


  const detailData = detailsResponse?.body?.kpis?.[0] || {};
  
  // Extract monthly view data
  const monthlyViewCharts = detailData?.detail?.monthlyView?.chart || [];
  const monthlyLineChart = monthlyViewCharts.find(
    (chart: any) => chart.chartType === "lineChart"
  );
  const monthlyTableChart = monthlyViewCharts.find(
    (chart: any) => chart.chartType === "table"
  );
  const monthlyLineChartData = monthlyLineChart?.chartDetail?.lineChartDetail;
  const monthlyTableData = monthlyTableChart?.chartDetail?.tableDetail;

  // Extract annually view data
  const annuallyViewCharts = detailData?.detail?.annuallyView?.chart || [];
  const annuallyTableChart = annuallyViewCharts.find(
    (chart: any) => chart.chartType === "table"
  );
  const annuallyTableData = annuallyTableChart?.chartDetail?.tableDetail;

  // Validate lineChartData has valid structure with lines and yAxis
  const hasValidLineChartData = isValidLineChartData(monthlyLineChartData);
  const lineChartData = hasValidLineChartData ? monthlyLineChartData : undefined;
  const hasChartData = hasValidLineChartData;

  // Use monthly table data if available, otherwise use annually table data
  const tableData = monthlyTableData || annuallyTableData;
  const currency = detailData?.kpiCurrency || "USD";

  useEffect(() => {
    if (!hasChartData) {
      setViewMode("right");
    }
  }, [hasChartData]);

  if (isLoading) return <Skeleton height="400px" />;

  
  const headerContent = (
    <div className={styles.header_content}>
      <div className={styles.tile_header}>
        <div className={styles.title}>{t("faaPoints.title")}</div>
        <div className={styles.desc}>{t("faaPoints.description")}</div>
      </div>
      {hasChartData && (
        <div className={styles.chart_view_wrapper}>
          <ViewModeSwitcher
            mode={viewMode}
            onChangeMode={setViewMode}
            disabled={false}
          />
        </div>
      )}
    </div>
  );

  const renderTable = () =>
    isEmpty(tableData) ? (
      <div className={styles.no_data}>{t("noDataAvailable")}</div>
    ) : (
      <GenericTable tableData={tableData} kpiCode={kpiCode} highlightColumns={[1]} />
    );

  const leftContent =
    hasChartData && viewMode === "left" && lineChartData ? (
      <GraphCard
        lineChartData={lineChartData}
        currency={currency}
        chartSummaryData={lineChartData?.chartSummary}
      />
    ) : (
      renderTable()
    );

  const rightContent = isLoadingAiSummary ? (
    <Skeleton height="200px" />
  ) : (
    <InsightAI
      updateDate=""
      content={aiContent}
      title={t("ai_summary")}
      showDropdown={false}
      isDropdownEnabled={true}
      flowerIconSize={24}
      titleSize={14}
    />
  );

  return (
    <KPIDetailTile
      headerContent={headerContent}
      leftContent={leftContent}
      rightContent={rightContent}
      onClose={onClose!}
      dataTestId="faa-points-detail"
    />
  );
};

export default FAAPointsTile;
